import { memo, useEffect, useMemo, useRef, useState } from "react";
import { renderArticleBody, type ImageDimensions } from "../../utils/articleHtml";

// Read-only article body, rendered from the stored HTML on the server and the
// client alike.
//
// This replaces ContentBodyTiptap, which mounted a full Tiptap editor
// (`editable: false`, `ssr: false`) just to re-serialise the stored HTML. That
// cost readers three things on every article: the body was absent from the
// server HTML, so the largest image could not paint until ~120 KB of editor
// code had loaded and parsed (the LCP "render delay"); the editor's detached
// ProseMirror DOM fetched every body image at its raw stored URL while the
// rendered copy fetched the same bytes at the normalised URL (the whole body
// downloaded twice); and the parse itself was the page's long task. The schema
// round-trip's formatting decisions live on in utils/articleNormalize.ts.
//
// `renderArticleBody` is deterministic, so the string computed here matches the
// server's byte for byte and hydration has nothing to reconcile. The two
// behaviours that need the browser stay as effects on the mounted DOM: the
// in-article AdSense slot and the click-to-load YouTube facades.

declare global {
  interface Window {
    adsbygoogle: { [key: string]: unknown }[];
  }
}

type Device = "mobile" | "tablet" | "desktop";

const AD_SLOTS: Record<Device, string> = {
  desktop: "6211961495",
  tablet: "4811249912",
  mobile: "9265953521",
};

const buildAdBlock = (device: Device): HTMLElement => {
  const wrapper = document.createElement("div");
  wrapper.className = "in-article-ad";
  const ins = document.createElement("ins");
  ins.className = "adsbygoogle";
  ins.style.cssText = "display:block; text-align:center;";
  ins.setAttribute("data-ad-layout", "in-article");
  ins.setAttribute("data-ad-format", "fluid");
  ins.setAttribute("data-ad-client", "ca-pub-6015892276154863");
  ins.setAttribute("data-ad-slot", AD_SLOTS[device]);
  wrapper.appendChild(ins);
  return wrapper;
};

const spacerParagraph = (): HTMLElement => {
  const p = document.createElement("p");
  p.innerHTML = "&nbsp;";
  return p;
};

// Same placement as the old string-based insertAdsIntoBlocks, on the live DOM:
// the body's top-level children are its blocks. Desktop/tablet: one slot at the
// middle (never before block 2, never after the penultimate). Mobile: after a
// random paragraph, padded by two blank lines. Fewer than four blocks: no ad.
const insertAd = (root: HTMLElement, device: Device) => {
  const blocks = Array.from(root.children).filter((el) => !el.classList.contains("in-article-ad"));
  if (blocks.length < 4) return;
  if (device === "mobile") {
    const paragraphs = blocks.filter((el) => el.tagName === "P");
    if (paragraphs.length < 2) return;
    const anchor = paragraphs[Math.floor(Math.random() * paragraphs.length)];
    const next = anchor.nextSibling;
    root.insertBefore(spacerParagraph(), next);
    root.insertBefore(spacerParagraph(), next);
    root.insertBefore(buildAdBlock(device), next);
    return;
  }
  const pos = Math.max(2, Math.min(Math.floor(blocks.length / 2), blocks.length - 2));
  root.insertBefore(buildAdBlock(device), blocks[pos]);
};

const isInsideHiddenLayout = (el: HTMLElement): boolean => {
  let current: HTMLElement | null = el.parentElement;
  while (current && current !== document.body) {
    if (window.getComputedStyle(current).display === "none") return true;
    current = current.parentElement;
  }
  return false;
};

const hasRenderableSize = (insEl: HTMLElement): boolean => {
  const insRect = insEl.getBoundingClientRect();
  const insStyle = window.getComputedStyle(insEl);
  if (
    insRect.width <= 0 ||
    insEl.clientWidth <= 0 ||
    insStyle.display === "none" ||
    insStyle.visibility === "hidden" ||
    (insEl.offsetParent === null && insStyle.position !== "fixed")
  ) {
    return false;
  }
  let current: HTMLElement | null = insEl;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    if (style.display === "none" || style.visibility === "hidden") return false;
    const rect = current.getBoundingClientRect();
    if (rect.width <= 0 || current.clientWidth <= 0) return false;
    current = current.parentElement;
  }
  return true;
};

const pushAd = (insEl: HTMLElement): boolean => {
  if (insEl.dataset.gadsInitialized === "1" || insEl.hasAttribute("data-ad-status")) return true;
  if (!hasRenderableSize(insEl)) return false;
  try {
    (window.adsbygoogle = window.adsbygoogle || []).push({});
    insEl.dataset.gadsInitialized = "1";
    return true;
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    if (message.includes("No slot size for availableWidth=0")) return false;
    console.error("AdSense in-article error:", e);
    return false;
  }
};

export const activateFacade = (el: HTMLElement) => {
  const id = el.getAttribute("data-yt-id");
  if (!id) return;
  const iframe = document.createElement("iframe");
  iframe.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1`;
  iframe.title = "YouTube video";
  iframe.loading = "lazy";
  iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
  iframe.allowFullscreen = true;
  iframe.style.cssText = "position:absolute;top:0;left:0;width:100%;height:100%;border:0";
  el.innerHTML = "";
  el.appendChild(iframe);
};

type Props = {
  contentBody: string;
  /** From getStaticProps' probe; absent on the client-fetch fallback, which is fine. */
  imageDimensions?: ImageDimensions | null;
};

const ContentBodyHtml = ({ contentBody, imageDimensions }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [device, setDevice] = useState<Device | null>(null);

  const html = useMemo(() => renderArticleBody(contentBody, imageDimensions ?? undefined), [contentBody, imageDimensions]);
  // React re-sets innerHTML whenever the dangerouslySetInnerHTML *object* changes
  // identity, not when the string does. An inline `{ __html }` would therefore
  // rebuild the whole body DOM on every re-render of the article (the reading
  // progress bar re-renders it on every scroll event), throwing away the
  // in-article ad slot and every node the observers below are watching. One
  // object per html string; and the component is memo()ed so parent re-renders
  // with the same props do not reach here at all.
  const htmlProp = useMemo(() => ({ __html: html }), [html]);

  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      setDevice(w < 768 ? "mobile" : w < 1024 ? "tablet" : "desktop");
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // In-article AdSense slot. Inserted into the mounted DOM (not by rewriting
  // innerHTML, which would re-create every <img> after first paint). Skipped
  // inside a display:none layout variant — adsbygoogle.push() scans every
  // uninitialised <ins> on the page and hidden ones throw availableWidth=0.
  useEffect(() => {
    const root = containerRef.current;
    if (!root || !device || !html) return;
    if (root.querySelector(".in-article-ad")) return;
    if (isInsideHiddenLayout(root)) return;
    insertAd(root, device);

    const wrappers = Array.from(root.querySelectorAll<HTMLElement>(".in-article-ad"));
    if (!wrappers.length) return;
    const intersected = new WeakSet<Element>();
    const tryPush = (wrapper: Element) => {
      const insEl = wrapper.querySelector("ins.adsbygoogle") as HTMLElement | null;
      return insEl ? pushAd(insEl) : false;
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          intersected.add(entry.target);
          requestAnimationFrame(() => {
            if (tryPush(entry.target)) observer.unobserve(entry.target);
          });
        });
      },
      { threshold: 0 },
    );
    wrappers.forEach((w) => observer.observe(w));

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const target = entry.target as HTMLElement;
          const wrapper = target.classList.contains("in-article-ad") ? target : (target.closest(".in-article-ad") as HTMLElement | null);
          if (!wrapper || !intersected.has(wrapper)) return;
          if (tryPush(wrapper)) observer.unobserve(wrapper);
        });
      });
      wrappers.forEach((w) => {
        resizeObserver?.observe(w);
        const insEl = w.querySelector("ins.adsbygoogle");
        if (insEl) resizeObserver?.observe(insEl);
      });
    }
    return () => {
      observer.disconnect();
      resizeObserver?.disconnect();
    };
  }, [device, html]);

  // Phone-deferred images (see deferredOnPhones in utils/articleHtml.ts): drop
  // the placeholder <source> once the picture is within 600px of the viewport
  // and the browser loads the real image. Without IntersectionObserver, load
  // everything at once — the same as before the deferral existed.
  useEffect(() => {
    const root = containerRef.current;
    if (!root || !html) return;
    const pictures = Array.from(root.querySelectorAll<HTMLElement>("picture[data-defer]"));
    if (!pictures.length) return;
    const reveal = (picture: HTMLElement) => {
      picture.querySelectorAll("source").forEach((s) => s.remove());
      picture.removeAttribute("data-defer");
    };
    if (typeof IntersectionObserver === "undefined") {
      pictures.forEach(reveal);
      return;
    }
    // Observe the <img>, not the <picture>: the wrapper is `display: contents`
    // (globals.scss) and so has no box for the observer to intersect.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const picture = (entry.target as HTMLElement).closest("picture");
          if (picture) reveal(picture as HTMLElement);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "600px 0px" },
    );
    pictures.forEach((p) => observer.observe(p.querySelector("img") ?? p));
    return () => observer.disconnect();
  }, [html]);

  // Click-to-load YouTube facades: swap the thumbnail placeholder for the real
  // cookieless iframe only when the reader clicks/activates it.
  useEffect(() => {
    const root = containerRef.current;
    if (!root || !html) return;
    const onClick = (e: Event) => {
      const target = (e.target as HTMLElement)?.closest?.(".yt-facade") as HTMLElement | null;
      if (target && root.contains(target)) activateFacade(target);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const target = (e.target as HTMLElement)?.closest?.(".yt-facade") as HTMLElement | null;
      if (target && root.contains(target)) {
        e.preventDefault();
        activateFacade(target);
      }
    };
    root.addEventListener("click", onClick);
    root.addEventListener("keydown", onKey);
    return () => {
      root.removeEventListener("click", onClick);
      root.removeEventListener("keydown", onKey);
    };
  }, [html]);

  if (!contentBody) return null;

  // `ProseMirror` is the class the article CSS keys on (globals.scss); it is a
  // styling hook now, not an editor.
  return (
    <div className="w-full">
      <div ref={containerRef} className="ProseMirror" dangerouslySetInnerHTML={htmlProp} />
    </div>
  );
};

export default memo(ContentBodyHtml);
